/**
 * FeesAndTotalsManager
 * Computes fees and totals from current settings and payment method, then
 * delivers the result to the consumer event hook and to the fees-and-totals
 * fragment via the command dispatcher.
 *
 * This class is not exposed to the consuming application.
 */

import { createLogger } from '@anddone/ajs-logger';
import type {
  FeesAndTotals,
  PaymentMethod,
  TransactionSettings,
  CommandDispatcher,
} from '@anddone/ajs-util';
import { PAYMENT_METHOD_CHANNEL_TYPE } from '@anddone/ajs-util';
import type { Events } from '../Events';
import type { SettingsState } from './SettingsState';

const logger = createLogger('SDK', 'FeesAndTotalsManager');

export class FeesAndTotalsManager {
  // Most-recently computed fees and totals; updated on init and on payment-method changes.
  #current: FeesAndTotals | null = null;

  constructor(
    private readonly settings: SettingsState,
    private readonly dispatcher: CommandDispatcher,
    private readonly events: Events,
  ) {}

  /**
   * Most-recently computed fees and totals, or null until first published.
   */
  public get current(): FeesAndTotals | null {
    return this.#current;
  }

  /**
   * Compute fees for the given payment method, emit `fees-and-totals-change`,
   * and send `update-fees-and-totals` to the fees-and-totals fragment.
   *
   * Returns early (with a debug log) when settings have not been loaded yet.
   * The dispatcher skips the command when the fees-and-totals fragment is not
   * registered; the consumer event hook fires regardless.
   *
   * @param paymentMethod - The active payment method
   */
  public publish(paymentMethod: PaymentMethod): void {
    const settings = this.settings.current;

    if (!settings) {
      logger.debug('publish', 'Skipped — settings not yet available.');
      return;
    }

    const fees = this.calculateFees(settings, paymentMethod);

    this.#current = fees;

    logger.info('publish', 'Fees calculated.', {
      paymentMethod,
      subtotal: fees.subtotal,
      technologyFee: fees.technologyFee,
      processingFee: fees.processingFee,
      total: fees.total,
    });

    this.events.emit('fees-and-totals-change', {
      feesAndTotals: fees,
    });

    const feesPayload = {
      subtotal: fees.subtotal,
      technologyFee: fees.technologyFee,
      processingFee: fees.processingFee,
      total: fees.total,
      // commandToken is stamped inside OrchestratorCommandManager.createCommandMessage;
      // the placeholder here is overwritten before postMessage is called.
      commandToken: '',
    };

    // Dispatch to the standalone fees-and-totals fragment as well as each full-checkout
    // variant that embeds fee display inline. The dispatcher skips targets that are not
    // currently registered, so it is safe to list all targets here unconditionally.
    this.dispatcher.dispatch('fees-and-totals', 'update-fees-and-totals', feesPayload);
    this.dispatcher.dispatch('full-card-checkout', 'update-fees-and-totals', feesPayload);
    this.dispatcher.dispatch('full-ach-checkout', 'update-fees-and-totals', feesPayload);
    this.dispatcher.dispatch('full-multi-channel-checkout', 'update-fees-and-totals', feesPayload);
  }

  // ─── Private fee calculation ──────────────────────────────────────────────

  /**
   * Round a value to 2 decimal places using integer math to avoid float drift.
   */
  private roundToCents(value: number): number {
    return Math.round(value * 100) / 100;
  }

  /**
   * Derive the fees and totals for a given payment method and settings.
   *
   * Technology fee logic:
   *   1. Resolve the primary channel type for the payment method via
   *      PAYMENT_METHOD_CHANNEL_TYPE (card → credit, ach → ach).
   *   2. Cross-reference `allowedTransactionTypes` to confirm the channel is
   *      permitted for this payment intent.
   *      - If allowed (or if `allowedTransactionTypes` is empty), filter fee
   *        entries whose `applicableTo` includes that channel.
   *      - If not allowed (e.g. SDK defaults to 'card' on an ACH-only intent),
   *        fall back to fee entries whose `applicableTo` intersects with the
   *        actually-allowed channel types.
   *   3. Each matching fee: defaultFixedValue + (subtotal × defaultPercentValue / 100).
   *
   * Processing fee is always 0. Total = subtotal + technologyFee + processingFee.
   */
  private calculateFees(
    settings: TransactionSettings,
    paymentMethod: PaymentMethod,
  ): FeesAndTotals {
    const subtotal = this.roundToCents(settings.amount);
    const primaryChannel = PAYMENT_METHOD_CHANNEL_TYPE[paymentMethod];

    const allowedChannelSet = this.settings.allowedChannelTypes;

    const effectiveChannel =
      allowedChannelSet.size === 0 || allowedChannelSet.has(primaryChannel) ? primaryChannel : null;

    let rawTechnologyFee = 0;
    for (const fee of settings.technologyFees) {
      const applies =
        effectiveChannel !== null
          ? fee.applicableTo.includes(effectiveChannel)
          : fee.applicableTo.some(c => allowedChannelSet.has(c));

      if (applies) {
        rawTechnologyFee += fee.defaultFixedValue + subtotal * (fee.defaultPercentValue / 100);
      }
    }

    const technologyFee = this.roundToCents(rawTechnologyFee);
    const processingFee = 0;
    const total = this.roundToCents(subtotal + technologyFee + processingFee);

    return Object.freeze({ subtotal, technologyFee, processingFee, total });
  }
}

Object.freeze(FeesAndTotalsManager.prototype);
